import { addMoney, multiplyMoney } from '@shared/lib/money';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useForm, useFormState } from 'react-final-form';

import {
	GdsServicesFragment,
	TravellerFieldEnum,
	TravellerProfileLightFragment,
	useGetTravellerProfilesQuery
} from '@websky/graphql';
import { getCustomer, getTravellers } from '../../../../store/order/selectors';
import { getCurrency } from '../../../../../cache';
import { Currency } from '../../../../../enums';
import { PassengerFormState } from '../../../../../PassengerForm/hooks';
import {
	AdditionalTravellerFieldEnum,
	CommonField,
	InputType,
	MaxBirthDates,
	NameField,
	PassengerField,
	SubsidyField
} from '../../../../../Passenger/types';
import { FormValues, Passenger as PassengerType, PassengerLabels } from '../../../../types';
import { PassengerState } from '../../../../store/passengers/reducers';
import { concatPassengerNames, useIsMobile } from '../../../../../utils';
import { getInputType } from './Passengers';
import { Money } from '../../../../../types';
import { getAccompanyingFields, subsidyFieldFactory } from './utils';
import { SubsidiesState } from '../../../../store/subsidies/reducers';
import { isSubsidyValidForPassenger } from '../../../../store/subsidies/utils';
import { NationalityKeyLabelMap } from '@entities/passengers';
import { max, min } from 'date-fns';
import { resolveDocTypeDefaultValue } from '@shared/lib/passengers';
import { findSortedAcceptableRules } from '../../../../../Passenger/utils';
import { isNameFieldType } from '../../../../../PassengerForm/utils';

export function usePassengers({
	passengers,
	labels,
	services,
	passengersState,
	isChangePassengers,
	onChangeFormSubscription,
	availableSubsidies,
	isAuthorized
}: {
	passengers: PassengerType[];
	labels: PassengerLabels;
	services: GdsServicesFragment['services'];
	passengersState: PassengerState[];
	isChangePassengers?: boolean;
	onChangeFormSubscription?: (values: FormValues) => void;
	availableSubsidies: SubsidiesState['availableSubsidiesByPassengers'];
	isAuthorized: boolean;
}) {
	const travellers = useSelector(getTravellers);
	const orderCustomer = useSelector(getCustomer);
	const nationalities: NationalityKeyLabelMap = new Map();

	const form = useForm<FormValues>();
	const { initialValues } = useFormState<PassengerFormState>();

	const isMobile = useIsMobile();

	const travellerProfilesQuery = useGetTravellerProfilesQuery({ skip: !isAuthorized });
	const travellerProfiles = travellerProfilesQuery.data?.TravellerProfiles ?? [];

	useEffect(() => {
		const unsubscribe = form.subscribe(
			({ values }) => {
				onChangeFormSubscription?.(values);
			},
			{ values: true }
		);

		return unsubscribe;
	}, [form]);

	const travellersMap = useMemo(() => {
		return new Map(travellers.map(traveller => [traveller.id, traveller]));
	}, [travellers]);

	const [currentCurrency, servicesMap] = useMemo(() => {
		let currency = getCurrency();

		const servicesByIdMap = new Map(
			services.map(service => {
				if (currency !== service.price?.currency) {
					currency = service.price.currency as Currency;
				}

				return [service.id, service];
			})
		);

		return [currency, servicesByIdMap];
	}, [services]);

	const [passengerLabelMap, passengerFieldsMap] = useMemo(() => {
		const labelMap = new Map<number, string>();
		const fieldsMap = new Map<number, PassengerField[]>();

		const { values } = form.getState();

		passengers.forEach((passenger, passengerId) => {
			const passengerValuesMap = new Map((passenger.values ?? []).map(value => [value.type, value]));

			let lastName: string;
			let firstName: string;

			const travellerBirthDateField = passengerValuesMap.get(TravellerFieldEnum.BirthDate);
			const docTypeFieldValidations = passengerValuesMap.get(TravellerFieldEnum.DocType)?.validationRules ?? [];

			const maxDates: Date[] = [];
			const minDates: Date[] = [];

			for (const rule of travellerBirthDateField?.validationRules ?? []) {
				const { maxDate, minDate } = rule;

				if (maxDate) {
					maxDates.push(new Date(maxDate));
				}

				if (minDate) {
					minDates.push(new Date(minDate));
				}
			}

			const maxBirthDates: MaxBirthDates = {
				max: max(maxDates),
				min: min(minDates)
			};

			passengerValuesMap.forEach(({ name, type, validationRules, value, isEditable }) => {
				if (type === TravellerFieldEnum.Nationality && validationRules?.[0]?.options?.length) {
					validationRules[0].options.map(({ value, label }) => {
						if (!nationalities.get(value)) {
							nationalities.set(value, label);
						}
					});
				}

				if (!lastName && type === TravellerFieldEnum.LastName) {
					lastName = value;
				}

				if (!firstName && type === TravellerFieldEnum.FirstName) {
					firstName = value;
				}

				const initialValue = (value ?? initialValues.passengers[passengerId]?.[name]) as string;
				const currentValue = values.passengers[passengerId]?.[name] as string;

				let defaultValue = initialValue;

				const shouldUseCurrentValue =
					currentValue &&
					(!initialValue || initialValue !== currentValue) &&
					(!isChangePassengers || !isNameFieldType(type));

				if (shouldUseCurrentValue) {
					defaultValue = currentValue;
				}

				if (type === TravellerFieldEnum.DocType) {
					const docTypeOptions = findSortedAcceptableRules(values.passengers[passengerId], validationRules)[0]
						?.options;

					defaultValue = resolveDocTypeDefaultValue(defaultValue, isEditable, docTypeOptions);
				}

				const field: CommonField = {
					name,
					readonly: !isEditable,
					type,
					input: getInputType(type),
					placeholder: name,
					validations: validationRules,
					defaultValue
				};

				if ([TravellerFieldEnum.FirstName, TravellerFieldEnum.LastName].includes(type)) {
					const nameField = field as NameField;
					nameField.nationalities = nationalities;
					nameField.maxBirthDates = maxBirthDates;
					nameField.travellerProfiles = travellerProfiles.reduce<TravellerProfileLightFragment[]>(
						(acc, traveller) => {
							const filteredDocs = traveller.documents.filter(doc => {
								if (!docTypeFieldValidations.length) {
									return true;
								}

								const docType =
									doc.values.find(v => v.type === TravellerFieldEnum.DocType)?.value ?? doc.type;
								const nationality =
									doc.values.find(v => v.type === TravellerFieldEnum.Citizenship)?.value ??
									doc.citizenship;

								return docTypeFieldValidations
									.filter(
										rule =>
											!rule.with ||
											rule.with.some(
												w =>
													w.type === TravellerFieldEnum.Nationality && w.value === nationality
											)
									)
									.some(rule => rule.options.some(option => option.value === docType));
							});

							if (filteredDocs.length > 0) {
								acc.push({ ...traveller, documents: filteredDocs });
							}

							return acc;
						},
						[]
					);
				}

				fieldsMap.set(passengerId, [...(fieldsMap.get(passengerId) ?? []), field]);
			});

			if (passenger.subsidyInfo || isChangePassengers) {
				const passengerFields = fieldsMap.get(passengerId) || [];

				if (passenger.subsidyInfo) {
					const availableSubsidiesForPassenger = availableSubsidies[passengerId];

					if (availableSubsidiesForPassenger && availableSubsidiesForPassenger.length > 0) {
						// Создаем ОДНО поле со всеми субсидиями
						passengerFields.push(subsidyFieldFactory(availableSubsidiesForPassenger, true));
					}
				}

				if (isChangePassengers) {
					passengerFields.push({
						name: 'files',
						readonly: false,
						type: AdditionalTravellerFieldEnum.ChangeTravellerFile,
						input: InputType.Text,
						placeholder: '',
						validations: [],
						defaultValue: null
					});
				}

				fieldsMap.set(passengerId, [...passengerFields]);
			}

			labelMap.set(
				passengerId,
				lastName && firstName ? concatPassengerNames({ lastName, firstName }) : labels[passengerId]
			);
		});

		return [labelMap, fieldsMap];
	}, [labels, passengers, isChangePassengers, initialValues, isMobile, travellerProfiles]);

	const fieldsMapWithSubsidies = useSubsidyFieldsChangerByFormValues(passengerFieldsMap, availableSubsidies);

	const servicesPricesMap = useMemo(() => {
		const prices = new Map<number, Money>();

		passengersState.forEach((passengerState, passengerId) => {
			let servicesPrice: Money = { amount: 0, currency: currentCurrency };

			passengerState.services.forEach(selectedService => {
				const service = servicesMap.get(selectedService.serviceId);

				if (service) {
					servicesPrice = addMoney(
						servicesPrice,
						multiplyMoney(service.price, selectedService.count * selectedService.segmentIds.length)
					);
				}
			});

			if (passengerState.seats) {
				passengerState.seats.forEach(seat => {
					if (seat.price) {
						servicesPrice = addMoney(servicesPrice, seat.price);
					}
				});
			}

			prices.set(passengerId, servicesPrice);
		});

		return prices;
	}, [passengersState, servicesMap, currentCurrency]);

	const accompanyingFields = useMemo(() => getAccompanyingFields(orderCustomer), [orderCustomer]);

	return {
		nationalities,
		travellersMap,
		passengerLabelMap,
		passengerFieldsMap: fieldsMapWithSubsidies,
		servicesPricesMap,
		accompanyingFields
	};
}

/**
 * @param passengerFieldsMap мапа текущих полей
 * @param availableSubsidiesByPassengers доступные субсидии по пассажирам
 *
 * проставляет для полей субсидий disabled true / false в зависимости от значений пассажира
 * добавляет дополнительные поля из выбранной субсидии в форму пассажира
 *
 * @example
 * если не заполнено имя / фамилия субсидия недоступна для выбора
 * неправильно заполнен снилс - субсидия недоступна для выбора (см subsidy.validation)
 * при выборе субсидии добавляются additionalFields из SubsidyOption
 */
export const useSubsidyFieldsChangerByFormValues = (
	passengerFieldsMap: Map<number, PassengerField[]>,
	availableSubsidiesByPassengers: SubsidiesState['availableSubsidiesByPassengers']
): Map<number, PassengerField[]> => {
	const { values } = useFormState<FormValues>({ subscription: { values: true } });

	const updatedFieldsMap = new Map<number, PassengerField[]>();

	for (const [passengerId, subsidies] of Object.entries(availableSubsidiesByPassengers)) {
		const passengerIdNum = parseInt(passengerId, 10);
		const passengerFields = passengerFieldsMap.get(passengerIdNum);

		if (!passengerFields) {
			continue;
		}

		// Получаем выбранную субсидию для этого пассажира
		const selectedSubsidyId = values.passengers?.[passengerIdNum]?.subsidy;

		const updatedFields: PassengerField[] = [];
		let subsidyFieldIndex = -1;

		// Обрабатываем все поля пассажира
		passengerFields.forEach((field, index) => {
			if (field.type !== AdditionalTravellerFieldEnum.Subsidy) {
				updatedFields.push(field);

				return;
			}

			subsidyFieldIndex = index;
			const subsidyField = field as SubsidyField;

			// Обновляем disabled для каждой опции
			const updatedOptions = subsidyField.options.map(option => {
				const subsidy = subsidies.find(s => s.id === option.value);

				if (!subsidy) {
					return { ...option, disabled: true };
				}

				const isValid = isSubsidyValidForPassenger(values['passengers'][passengerIdNum], subsidy, [
					TravellerFieldEnum.Snils
				]);

				return {
					...option,
					disabled: !isValid
				};
			});

			const updatedValidations = subsidyField.validations ? [...subsidyField.validations] : [];
			if (updatedValidations[0]) {
				updatedValidations[0] = {
					...updatedValidations[0],
					options: updatedOptions.map(opt => ({
						label: opt.label,
						value: opt.value
					}))
				};
			}

			updatedFields.push({
				...subsidyField,
				options: updatedOptions,
				validations: updatedValidations
			});
		});

		// Добавляем дополнительные поля из выбранной субсидии
		if (selectedSubsidyId && subsidyFieldIndex !== -1) {
			const subsidyField = updatedFields[subsidyFieldIndex] as SubsidyField;
			const selectedOption = subsidyField.options.find(opt => opt.value === selectedSubsidyId);

			if (selectedOption?.additionalFields && selectedOption.additionalFields.length > 0) {
				// Получаем имена полей, которые уже есть в форме (до поля subsidy)
				const existingFieldNames = new Set(updatedFields.slice(0, subsidyFieldIndex).map(f => f.name));

				// Добавляем только те дополнительные поля, которых еще нет
				const fieldsToAdd = selectedOption.additionalFields.filter(
					additionalField => !existingFieldNames.has(additionalField.name)
				);

				// Вставляем дополнительные поля сразу после поля subsidy
				updatedFields.splice(subsidyFieldIndex + 1, 0, ...fieldsToAdd);
			}
		}

		updatedFieldsMap.set(passengerIdNum, updatedFields);
	}

	// Копируем пассажиров, у которых нет субсидий
	for (const [passengerId, fields] of passengerFieldsMap.entries()) {
		if (!updatedFieldsMap.has(passengerId)) {
			updatedFieldsMap.set(passengerId, fields);
		}
	}

	return updatedFieldsMap;
};
